import { initializeApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import L from "leaflet";
import { jsPDF } from "jspdf";
import dayjs from "dayjs";
import "svg2pdf.js";

import config from "config/config";
import { beautifyNumber } from "utils/numberModification";
import northArrowImage from "../../assets/images/print/north-arrow.png";
import { setPrintLegendGrafic } from "features/map/utils/legendPrintConversion";
import { FirestoreLayerLegend } from "types/firestoreFeatures";
import { IconID } from "types/auth";
import { loadImage } from "utils/fileUtils";
import { toPngDataUrl } from "utils/pdfUtils";

const firebaseApp = initializeApp(config.FIREBASE_API);
const functions = getFunctions(firebaseApp, "europe-west3");
const imageProxy = httpsCallable(functions, "printMap-imageProxy");

// eslint-disable-next-line @typescript-eslint/no-var-requires
const domtoimage = require("@foresting-tomorrow/dom-to-image");

// ---------- Tweaks you can tune ----------
const TARGET_DPI = 200; // 200–300 is typical for map prints
const JPEG_QUALITY = 0.85; // 0.75–0.9 usually looks good
// ----------------------------------------

const printImgCache = new Map<string, string>();
const printImgInflight = new Map<string, Promise<string>>();
// A valid transparent image keeps dom-to-image rendering when an individual
// remote tile cannot be fetched through the print proxy.
const TRANSPARENT_IMAGE_DATA_URL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

export type PaperSize = "A4" | "A3" | "A2";
type PaperSpec = { widthMM: number; heightMM: number };
type Orientation = "Landscape" | "Portrait";
type LengthUnit = "MILIMETER" | "CENTIMETER" | "METER" | "KILOMETER";

export type ScaleRatio = {
  fromUnit: LengthUnit;
  fromLength: number;
  toUnit: LengthUnit;
  toLength: number;
};

type PrintArea = {
  main: L.Rectangle;
  sidebar: L.Rectangle;
  scaleRatioMarker: L.Marker;
};

export type LegendElement = {
  name: string;
  abr?: string;
  cat?: string;
  color: string;
  iconId?: IconID;
  layerId?: string;
  type: FirestoreLayerLegend["type"] | "Point";
};

export type PrintMetaData = {
  intlText: {
    title: string;
    legend: string;
    map: string;
    project: string;
    activity: string;
    estate: string;
    timeStamp: string;
  };
  mapName?: string;
  projectName?: string;
  activityName?: string;
  estateName?: string;
  printFooter: string;
  printLogo?: string;
};

type PrintExclusion = {
  layer: L.Layer;
  label: string;
  reason: string;
  ok: number;
  bad: number;
};

type LayerTempHealth = {
  label: string;
  ok: number;
  bad: number;
  timedOut: number;
  consecutiveFailCycles: number;
  lastReason?: string;
  failed: boolean;
};

export type onPrintLayerExclusionsCallbackProps = {
  excluded: PrintExclusion[];
};

export type PrintLayerTimeout = {
  label: string;
};

export type onPrintLayerTimeoutCallbackProps = {
  timedOut: PrintLayerTimeout[];
};

export type onPrintImageFailuresCallbackProps = {
  layerLabels: string[];
  unmatchedCount: number;
  /** Kept for existing callback consumers; prefer layerLabels for UI text. */
  count: number;
};

export type PrintOptions = {
  orientation: Orientation;
  paperSize: PaperSize;
  screenPercentage: number;
  enableLegend: boolean;
  scaleRatios: Array<ScaleRatio>;
  quality: number; // DOM capture scale (keep as you had it)
  documentTitle: string;
  lockPrintScale: boolean;
  onPrintLayerExclusions?: ({
    excluded,
  }: onPrintLayerExclusionsCallbackProps) => void;
  /** Called when a visible map layer did not finish loading before capture. */
  onPrintLayerTimeouts?: ({
    timedOut,
  }: onPrintLayerTimeoutCallbackProps) => void;
  /** Called when individual remote map images were omitted during capture. */
  onPrintImageFailures?: ({
    count,
  }: onPrintImageFailuresCallbackProps) => void;
};

export type PrintMode = {
  exit: () => void;
  redraw: () => ScaleRatio;
  print: (
    element: LegendElement[],
    currentScaleRatio: ScaleRatio,
    metaData: PrintMetaData,
    withoutSave?: boolean
  ) => Promise<jsPDF>;
  printArea: PrintArea;
};

const PRINT_PANE_NAME = "print-pane";
const PRINT_PANE_COLOR = "black";
const PRINT_PANE_FILLCOLOR = "white";
const PRINT_PANE_DASH_WEIGHT = 1;
const PRINT_PAN_Z_INDEX = "9999";
const PRINT_STYLE = {
  fillColor: PRINT_PANE_FILLCOLOR,
  color: PRINT_PANE_COLOR,
  pane: PRINT_PANE_NAME,
  weight: PRINT_PANE_DASH_WEIGHT,
};

// length in mm
const SIDE_BAR_WIDTH = 69;

export const nextPaperSize = (paper: PaperSize): PaperSize =>
  ({
    A4: "A3" as PaperSize,
    A3: "A2" as PaperSize,
    A2: "A4" as PaperSize,
  }[paper]);

const paperSizeToPaperSpec = (paper: PaperSize): PaperSpec =>
  ({
    A4: { widthMM: 210, heightMM: 297 },
    A3: { widthMM: 297, heightMM: 420 },
    A2: { widthMM: 420, heightMM: 594 },
  }[paper]);

const rotatePaperSpec = (ps: PaperSpec): PaperSpec => ({
  widthMM: ps.heightMM,
  heightMM: ps.widthMM,
});

const getPaperSpec = (pf: PaperSize, o: Orientation): PaperSpec => {
  const ps = paperSizeToPaperSpec(pf);
  return (o === "Landscape" && ps.heightMM < ps.widthMM) ||
    (o === "Portrait" && ps.widthMM <= ps.heightMM)
    ? ps
    : rotatePaperSpec(ps);
};

const scaleRatio = (
  map: L.Map,
  paperSize: PaperSize,
  orientation: Orientation,
  bounds: L.LatLngBounds
): ScaleRatio => {
  const printAreaDistM = map.distance(
    bounds.getSouthWest(),
    bounds.getSouthEast()
  );
  const paperDistCM = getPaperSpec(paperSize, orientation).widthMM / 10;
  const sr = printAreaDistM / paperDistCM;

  return {
    fromUnit: "CENTIMETER",
    fromLength: 1,
    toUnit: "METER",
    toLength: sr,
  };
};

const showScaleRatio = (sr: ScaleRatio): string =>
  `${beautifyNumber(sr.toLength * 100, 0)}`;

const lengthToSI = (length: number, unit: LengthUnit): number =>
  ({
    MILIMETER: length / 1000,
    CENTIMETER: length / 100,
    METER: length,
    KILOMETER: length * 1000,
  }[unit]);

const scaleRationToSI = (sr: ScaleRatio): ScaleRatio => ({
  fromUnit: "METER",
  fromLength: lengthToSI(sr.fromLength, sr.fromUnit),
  toUnit: "METER",
  toLength: lengthToSI(sr.toLength, sr.toUnit),
});

const compareScaleRatio = (sr1: ScaleRatio, sr2: ScaleRatio): number => {
  const sisr1 = scaleRationToSI(sr1);
  const sisr2 = scaleRationToSI(sr2);
  const ordering =
    sisr2.fromLength / sisr2.toLength - sisr1.fromLength / sisr1.toLength;
  return ordering;
};

const maxLowerBound = <A>(
  compareFn: (a: A, b: A) => number,
  as: Array<A>,
  a: A
) => {
  let lowerBound = a;
  for (const _a of as)
    if (compareFn(_a, a) <= 0) lowerBound = _a;
    else break;
  return lowerBound;
};

const fixBoundsWidth = (
  map: L.Map,
  bounds: L.LatLngBounds,
  opt: PrintOptions,
  width: number
): L.LatLngBounds => {
  const corner1 = map.latLngToLayerPoint(bounds.getNorthWest());
  const corner2 = map.latLngToLayerPoint(bounds.getSouthEast());
  const pixelsPerMM =
    (corner2.x - corner1.x) /
    getPaperSpec(opt.paperSize, opt.orientation).widthMM;
  const newWidth = width * pixelsPerMM;
  const newCorner2 = L.point(corner1.x + newWidth, corner2.y);
  return L.latLngBounds(
    map.layerPointToLatLng(corner1),
    map.layerPointToLatLng(newCorner2)
  );
};

const printAreaCoords = (
  map: L.Map,
  opt: PrintOptions
): { bounds: L.LatLngBounds; scaleRatio: ScaleRatio } => {
  const screenPercentage = Math.min(100, Math.max(0, opt.screenPercentage));
  const pixelCenter = map.latLngToLayerPoint(map.getCenter());
  const pixelBounds = map.getPixelBounds();

  const paperSpec = getPaperSpec(opt.paperSize, opt.orientation);
  const height2width = paperSpec.widthMM / paperSpec.heightMM;

  const screenW = pixelBounds.getBottomRight().x - pixelBounds.getTopLeft().x;
  const screenH = pixelBounds.getBottomRight().y - pixelBounds.getTopLeft().y;

  const scaledRectH = (screenH * screenPercentage) / 100;
  const scaledRectW = (screenW * screenPercentage) / 100;
  const [rectW, rectH] =
    screenW - scaledRectW < screenH - scaledRectH
      ? [scaledRectW, scaledRectW / height2width]
      : [scaledRectH * height2width, scaledRectH];

  const corner1 = L.point(pixelCenter.x - rectW / 2, pixelCenter.y + rectH / 2);
  const corner2 = L.point(pixelCenter.x + rectW / 2, pixelCenter.y - rectH / 2);
  const printAreaBounds = L.latLngBounds(
    map.layerPointToLatLng(corner1),
    map.layerPointToLatLng(corner2)
  );

  const currentSRSI = scaleRationToSI(
    scaleRatio(map, opt.paperSize, opt.orientation, printAreaBounds)
  );

  if (opt.lockPrintScale)
    return { bounds: printAreaBounds, scaleRatio: currentSRSI };

  const bestFitSR = maxLowerBound(
    compareScaleRatio,
    opt.scaleRatios,
    currentSRSI
  );
  const bestFitSRSI = scaleRationToSI(bestFitSR);
  const ratioMultiplier =
    currentSRSI.fromLength /
    currentSRSI.toLength /
    (bestFitSRSI.fromLength / bestFitSRSI.toLength);

  const fittedRectW = rectW * ratioMultiplier;
  const fittedRectH = rectH * ratioMultiplier;
  const fittedCorner1 = L.point(
    pixelCenter.x - fittedRectW / 2,
    pixelCenter.y + fittedRectH / 2
  );
  const fittedCorner2 = L.point(
    pixelCenter.x + fittedRectW / 2,
    pixelCenter.y - fittedRectH / 2
  );
  const fittedPrintAreaBounds = L.latLngBounds(
    map.layerPointToLatLng(fittedCorner1),
    map.layerPointToLatLng(fittedCorner2)
  );

  return { bounds: fittedPrintAreaBounds, scaleRatio: bestFitSR };
};

const divIcon = (text: string): L.DivIcon =>
  L.divIcon({
    html: `<div style="position: absolute; left: 0px; bottom: 0px; z-index: 10; background-color: rgba(0, 0, 0, 0.6); display: flex; align-items: flex-end; padding: 1px 4px 1px 4px">
        <span style="font-size: 11px; color: white; margin-right: 1px;">1</span>
        <span style="font-size: 11px; color: white; margin-right: 1px;">:</span>
        <span style="font-size: 11px; color: white; margin-left: 0.5px; margin-right: 0.5px;">${text}</span>
    </div>`,
    className: "custom-div-icon",
    iconSize: [0, 0],
  });

const redraw = (map: L.Map, opt: PrintOptions, printArea: PrintArea) => {
  const { bounds, scaleRatio } = printAreaCoords(map, opt);
  printArea.main.setBounds(bounds);
  printArea.sidebar.setBounds(
    fixBoundsWidth(map, bounds, opt, opt.enableLegend ? SIDE_BAR_WIDTH : 0)
  );
  printArea.scaleRatioMarker.setLatLng(
    printArea.main.getBounds().getSouthWest()
  );
  printArea.scaleRatioMarker.setIcon(divIcon(showScaleRatio(scaleRatio)));
  return scaleRatio;
};

const print = async (
  map: L.Map,
  opt: PrintOptions,
  printArea: PrintArea,
  legendElements: LegendElement[],
  scaleRatioObj: ScaleRatio,
  metaData: PrintMetaData,
  withoutSave?: boolean
): Promise<jsPDF> => {
  const center = map.getCenter();
  const zoom = map.getZoom();
  const printAreaBounds = printArea.main.getBounds();
  // Keep a failed external layer off the map from the time it is identified
  // until after the DOM capture.  In particular, do not start observing it
  // only after the normal tile-settling wait has already consumed its errors.
  let isolatedLayers: Awaited<ReturnType<typeof excludeBadLayersForPrint>> | null =
    null;
  let removedLayerIds: string[] = [];

  if (!printAreaBounds.isValid()) {
    throw new Error("Invalid print area bounds");
  }
  map.fitBounds(printAreaBounds, { animate: false });

  await fitToTopLeft();
  return await printAndReset();

  async function fitToTopLeft() {
    const tlPA = map.latLngToContainerPoint(printAreaBounds.getNorthWest());
    const tlMap = map.latLngToContainerPoint(map.getBounds().getNorthWest());
    const c = map.latLngToContainerPoint(map.getCenter());
    const newC = L.point(c.x + tlPA.x - tlMap.x, c.y + tlPA.y - tlMap.y);
    map.panTo(map.containerPointToLatLng(newC), { animate: false });
    // 1) Let Leaflet finalize the view
    // Let Leaflet create the new viewport's WMS tiles before attaching the
    // monitors. A single animation frame is too early for several providers,
    // which resulted in a PDF containing only the already-cached background.
    await settleView(map, { softDelayMs: 500 });
    // 2) Start the per-layer error monitors *before* waiting. This lets us
    // remove one broken print-excludable WMS layer while retaining every
    // healthy layer, instead of allowing the broken tile to fail the entire
    // DOM snapshot.
    isolatedLayers = await excludeBadLayersForPrint(map, opt);
    removedLayerIds = isolatedLayers.removed.flatMap((exclusion) => {
      const id = (exclusion.layer as any).options?.id;
      return id ? [id] : [];
    });
    if (isolatedLayers.removed.length > 0) {
      opt.onPrintLayerExclusions?.({ excluded: isolatedLayers.removed });
    }
    // A timed-out excludable layer is already reported above as skipped; do
    // not show a second, less useful "incomplete" notification for it.
    const skippedLabels = new Set(
      isolatedLayers.removed.map((layer) => layer.label)
    );
    const incompleteLayers = isolatedLayers.timedOut.filter(
      (layer) => !skippedLabels.has(layer.label)
    );
    if (incompleteLayers.length > 0) {
      opt.onPrintLayerTimeouts?.({ timedOut: incompleteLayers });
    }

    // The isolation check only monitors layers which can be safely removed.
    // Still wait for every remaining visible tile layer before capturing: the
    // healthy thematic WMS layers otherwise often have not painted yet and
    // disappear from an otherwise successful PDF.
    const remainingLayerResults = await waitAllTilesQuiet(map, {
      timeout: 15_000,
      quietMs: 400,
    });
    const remainingTimedOut = remainingLayerResults
      .filter((result) => result.status === "timedOut")
      .map((result) => ({ label: getLayerLabel(result.layer) }))
      .filter((layer) => !skippedLabels.has(layer.label));
    if (remainingTimedOut.length > 0) {
      opt.onPrintLayerTimeouts?.({ timedOut: remainingTimedOut });
    }
  }

  async function printAndReset(): Promise<jsPDF> {
    const container = map.getContainer();
    const topLeft = map.latLngToContainerPoint(printAreaBounds.getNorthWest());
    const bottomRight = map.latLngToContainerPoint(
      printAreaBounds.getSouthEast()
    );
    const pixelW = bottomRight.x - topLeft.x;
    const pixelH = bottomRight.y - topLeft.y;
    const skippedImageLayers = new Map<string, string[]>();

    // const imgGet = async (uri: string) => {
    //   const res = await imageProxy({ uri });
    //   return res.data as string;
    // };
    // const imgGet = (() => {
    //   const cache = new Map<string, string>();
    //   const inflight = new Map<string, Promise<string>>();

    //   return async (uri: string) => {
    //     const c = cache.get(uri);
    //     if (c) return c;

    //     const r = inflight.get(uri);
    //     if (r) return r;

    //     const p = (async () => {
    //       const res = await imageProxy({ uri });
    //       const data = res.data as string;
    //       cache.set(uri, data);
    //       return data;
    //     })();

    //     inflight.set(uri, p);
    //     try {
    //       return await p;
    //     } finally {
    //       inflight.delete(uri);
    //     }
    //   };
    // })();

    async function imgGetCached(uri: string) {
      const c = printImgCache.get(uri);
      if (c) return c;

      const r = printImgInflight.get(uri);
      if (r) return r;

      const p = (async () => {
        try {
          const res = await imageProxy({ uri });
          const data = res.data as string;
          printImgCache.set(uri, data);
          return data;
        } catch (error) {
          // Do not let one unavailable tile reject the complete PDF capture.
          // The caller is informed after capture, without naming healthy
          // layers as failed.
          console.warn("Map image omitted from print", { uri, error });
          skippedImageLayers.set(uri, getLayerLabelsForImageUri(map, uri));
          return TRANSPARENT_IMAGE_DATA_URL;
        }
      })();

      printImgInflight.set(uri, p);
      try {
        return await p;
      } finally {
        printImgInflight.delete(uri);
      }
    }

    // Hide overlays before capture
    printArea.main.remove();
    printArea.sidebar.remove();
    printArea.scaleRatioMarker.remove();

    // Capture whole container at requested DOM scale (opt.quality)
    // As a second guard: ensure no late tile pops happened
    // await waitAllTilesQuiet(map, { timeout: 10000, quietMs: 300 });
    const captureSnapshot = () =>
      domtoimage.toPng(container, {
        imgGet: imgGetCached,
        quality: 1,
        bgcolor: "white",
        width: container.clientWidth * opt.quality,
        height: container.clientHeight * opt.quality,
        style: {
          transform: `scale(${opt.quality})`,
          transformOrigin: "top left",
        },
      });

    let snapshotDataUrl: any = null;
    let fallbackExclusions: PrintExclusion[] = [];
    try {
      snapshotDataUrl = await captureSnapshot();
      await document.fonts.load(`48px 'Material Icons'`);
    } catch (firstCaptureError) {
      // Tile events are not reliable for every WMS provider. If the renderer
      // itself rejects a special-layer image, retry once without every
      // print-excludable layer. This is the last-resort path that ensures a
      // usable base map PDF is produced whenever possible.
      fallbackExclusions = removeAllPrintExcludableLayers(map);
      if (fallbackExclusions.length > 0) {
        removedLayerIds = [
          ...removedLayerIds,
          ...fallbackExclusions.flatMap((exclusion) => {
            const id = (exclusion.layer as any).options?.id;
            return id ? [id] : [];
          }),
        ];
        try {
          await raf();
          snapshotDataUrl = await captureSnapshot();
          await document.fonts.load(`48px 'Material Icons'`);
        } catch (retryError) {
          restoreRemovedLayers(map, fallbackExclusions);
          isolatedLayers?.restore();
          map.setView(center, zoom, { animate: false });
          throw retryError;
        }
      } else {
        // Restore layers + detach monitors/patches
        isolatedLayers?.restore();
        // Set map back to original view
        map.setView(center, zoom, { animate: false });
        throw firstCaptureError;
      }
    }

    // Restore layers + detach monitors/patches
    restoreRemovedLayers(map, fallbackExclusions);
    isolatedLayers?.restore();
    isolatedLayers = null;

    if (skippedImageLayers.size > 0) {
      const layerLabels = Array.from(
        new Set(
          Array.from(skippedImageLayers.values()).flatMap((labels) => labels)
        )
      );
      const unmatchedCount = Array.from(skippedImageLayers.values()).filter(
        (labels) => labels.length === 0
      ).length;
      opt.onPrintImageFailures?.({
        layerLabels,
        unmatchedCount,
        count: skippedImageLayers.size,
      });
    }

    // Restore overlays
    printArea.main.addTo(map);
    printArea.sidebar.addTo(map);
    printArea.scaleRatioMarker.addTo(map);

    // --- Crop to print area (in the captured, scaled pixel space) ---
    const fullImg = await loadImage(snapshotDataUrl);

    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = pixelW * opt.quality;
    cropCanvas.height = pixelH * opt.quality;
    const cropCtx = cropCanvas.getContext("2d")!;
    cropCtx.drawImage(
      fullImg,
      topLeft.x * opt.quality,
      topLeft.y * opt.quality,
      cropCanvas.width,
      cropCanvas.height,
      0,
      0,
      cropCanvas.width,
      cropCanvas.height
    );

    // --- Resample once to target DPI for the page size ---
    const paperSpec = getPaperSpec(opt.paperSize, opt.orientation);
    const targetPxW = Math.round((paperSpec.widthMM / 25.4) * TARGET_DPI);
    const targetPxH = Math.round((paperSpec.heightMM / 25.4) * TARGET_DPI);

    const outCanvas = document.createElement("canvas");
    outCanvas.width = targetPxW;
    outCanvas.height = targetPxH;
    const outCtx = outCanvas.getContext("2d")!;
    outCtx.imageSmoothingEnabled = true;
    outCtx.imageSmoothingQuality = "high";
    outCtx.drawImage(
      cropCanvas,
      0,
      0,
      cropCanvas.width,
      cropCanvas.height,
      0,
      0,
      outCanvas.width,
      outCanvas.height
    );

    // --- Encode as JPEG for smaller file size ---
    const outDataUrl = outCanvas.toDataURL("image/jpeg", JPEG_QUALITY);

    // --- Build PDF with compression on ---
    const doc = new jsPDF({
      orientation: opt.orientation === "Landscape" ? "l" : "p",
      unit: "mm",
      format: [paperSpec.widthMM, paperSpec.heightMM],
      compress: true,
    });

    // Place map image to fill the page
    doc.addImage(
      outDataUrl,
      "JPEG",
      0,
      0,
      paperSpec.widthMM,
      paperSpec.heightMM
    );

    // -------------------------------
    // Decorations (unchanged)
    // -------------------------------
    const borderWidthThin = 0.1;
    const borderWidth = 0.28;
    const containerPadding = 1;
    const elementsOffset = 4;
    const textScaleFactor = 0.35;
    let maxLines = 35; // A4
    if (opt.paperSize === "A4" && opt.orientation === "Portrait") maxLines = 54;
    if (opt.paperSize === "A3")
      maxLines = opt.orientation === "Portrait" ? 82 : 54;
    if (opt.paperSize === "A2")
      maxLines = opt.orientation === "Portrait" ? 120 : 82;

    let truncAbr = 52;
    let truncNoAbr = 56;
    if (legendElements.length - 2 >= maxLines) {
      truncAbr = 16;
      truncNoAbr = 20;
    }

    // north arrow
    const imageWidth = 4.06;
    const imageHeight = 4.5;
    const imageContainerXPos = paperSpec.widthMM - elementsOffset;
    const imageContainerYPos = elementsOffset;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(borderWidth);
    doc.rect(
      imageContainerXPos,
      imageContainerYPos,
      -imageWidth - 2 * containerPadding,
      imageHeight + 2 * containerPadding,
      "FD"
    );
    doc.addImage(
      northArrowImage,
      "PNG",
      imageContainerXPos - imageWidth - containerPadding,
      imageContainerYPos + containerPadding + borderWidth,
      imageWidth,
      imageHeight
    );

    // legend section
    doc.setFontSize(7);
    if (opt.enableLegend) {
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, SIDE_BAR_WIDTH, paperSpec.heightMM, "F");
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(borderWidth);
      doc.line(SIDE_BAR_WIDTH, 0, SIDE_BAR_WIDTH, paperSpec.heightMM);

      doc.setFont("helvetica", "bold");
      doc.text(metaData.intlText.title, 5, 7);
      doc.setFont("helvetica", "normal");

      let offset = 12;
      if (metaData.estateName) {
        doc.text(
          `${metaData.intlText.estate}: ${metaData.estateName}`,
          5,
          offset
        );
        offset += 4;
      }
      if (metaData.mapName) {
        doc.text(`${metaData.intlText.map}: ${metaData.mapName}`, 5, offset);
        offset += 4;
      }
      if (metaData.projectName) {
        doc.text(
          `${metaData.intlText.project}: ${metaData.projectName}`,
          5,
          offset
        );
        offset += 4;
      }
      if (metaData.activityName) {
        doc.text(
          `${metaData.intlText.activity}: ${metaData.activityName}`,
          5,
          offset
        );
        offset += 4;
      }
      doc.text(
        `${metaData.intlText.timeStamp}: ${dayjs().format(
          "DD.MM.YYYY - HH:mm"
        )}`,
        5,
        offset
      );

      // legends
      offset += 8;
      doc.setFont("helvetica", "bold");
      doc.text(metaData.intlText.legend, 5, offset);
      offset += 2.6;
      doc.setFont("helvetica", "normal");

      let xPos = 5;
      const yStartPos = offset;
      let yPos = yStartPos;

      legendElements.forEach((element, i) => {
        const { name, abr, cat, layerId } = element;
        if (layerId && removedLayerIds.includes(layerId)) {
          return; // skip excluded layers
        }
        setPrintLegendGrafic({
          legend: element,
          doc,
          options: { xPos, yPos, borderWidth },
        });
        const nameStr = `${name}${cat ? ` (${cat})` : ""}`;
        doc.setFontSize(6);
        if (abr) {
          doc.text(abr, xPos + 4, yPos + 2.3);
          const abrTextWidth =
            doc.getStringUnitWidth(abr) * doc.getFontSize() * textScaleFactor;
          doc.text("-", xPos + 4 + abrTextWidth + 0.7, yPos + 2.2);
          if (nameStr.length > truncAbr)
            doc.text(
              nameStr.substring(0, truncAbr) + "...",
              xPos + 4 + abrTextWidth + 1.8,
              yPos + 2.3
            );
          else
            doc.text(
              nameStr.substring(0, truncAbr),
              xPos + 4 + abrTextWidth + 1.8,
              yPos + 2.3
            );
        } else {
          if (nameStr.length > truncNoAbr)
            doc.text(
              nameStr.substring(0, truncNoAbr) + "...",
              xPos + 4,
              yPos + 2.3
            );
          else doc.text(nameStr.substring(0, truncNoAbr), xPos + 4, yPos + 2.3);
        }
        if (i >= maxLines) {
          xPos = 37;
          yPos += 4.5;
          if (i === maxLines) yPos = yStartPos;
        } else {
          yPos += 4.5;
        }
      });
    }

    // ruler & scale
    doc.setFontSize(6);
    const paperSizeText = opt.paperSize;
    const paperSizeTextWidth =
      doc.getStringUnitWidth(paperSizeText) *
      doc.getFontSize() *
      textScaleFactor;
    const ratioText = "1:" + beautifyNumber(scaleRatioObj.toLength * 100, 0);
    const ratioTextWidth =
      doc.getStringUnitWidth(ratioText) * doc.getFontSize() * textScaleFactor;
    const rulerWidth = 30;
    const lengthInM = 3 * scaleRatioObj.toLength;
    const rulerText =
      lengthInM >= 1000
        ? beautifyNumber(lengthInM, 0) + " km"
        : beautifyNumber(lengthInM, 0) + " m";
    const rulerTextWidth =
      doc.getStringUnitWidth(rulerText) * doc.getFontSize() * textScaleFactor;

    const scaleContainerWidth =
      7 * containerPadding +
      paperSizeTextWidth +
      ratioTextWidth +
      rulerWidth +
      rulerTextWidth;
    const rulerStopHeight = 1.4;
    const scaleContainerHeight = 1.5 + 2 * containerPadding;
    const scaleXPos = paperSpec.widthMM - elementsOffset;
    const scaleYPos = paperSpec.heightMM - elementsOffset;

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(borderWidth);
    doc.rect(
      scaleXPos,
      scaleYPos,
      -scaleContainerWidth,
      -scaleContainerHeight,
      "FD"
    );

    const paperSizeTextXPos = scaleXPos - containerPadding - paperSizeTextWidth;
    const ratioTextXPos = paperSizeTextXPos - containerPadding - ratioTextWidth;
    const rulerXPos = ratioTextXPos - 3 * containerPadding - rulerWidth;
    const rulerTextXPos = rulerXPos - containerPadding - rulerTextWidth;

    doc.setFont("helvetica", "normal");
    doc.text(paperSizeText, paperSizeTextXPos, scaleYPos - containerPadding);
    doc.text(ratioText, ratioTextXPos, scaleYPos - containerPadding);

    doc.setFillColor(0, 0, 0);
    doc.rect(
      rulerXPos,
      scaleYPos - containerPadding - borderWidth,
      rulerWidth,
      borderWidth,
      "F"
    );
    doc.rect(
      rulerXPos,
      scaleYPos - containerPadding - rulerStopHeight,
      borderWidthThin,
      rulerStopHeight,
      "F"
    );
    doc.rect(
      rulerXPos + rulerWidth * 0.33,
      scaleYPos - containerPadding - rulerStopHeight * 0.66,
      borderWidthThin,
      rulerStopHeight / 2,
      "F"
    );
    doc.rect(
      rulerXPos + rulerWidth * 0.66,
      scaleYPos - containerPadding - rulerStopHeight * 0.66,
      borderWidthThin,
      rulerStopHeight / 2,
      "F"
    );
    doc.rect(
      rulerXPos + rulerWidth - borderWidthThin,
      scaleYPos - containerPadding - rulerStopHeight,
      borderWidthThin,
      rulerStopHeight,
      "F"
    );

    doc.text(rulerText, rulerTextXPos, scaleYPos - containerPadding);

    // footer
    // Get print logo
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");

    const maxHeight = 7.5; // mm
    const margin = 4;
    const offset = 3.5;

    let pngDataUrl: string | null = null;
    let computedWidth = 0;

    if (metaData.printLogo) {
      // Convert SVG/URL/data-url -> PNG data-url
      const { dataUrlPng, imgWidth, imgHeight } = await toPngDataUrl(
        metaData.printLogo
      );
      pngDataUrl = dataUrlPng;
      computedWidth = maxHeight * (imgWidth / imgHeight);
    }

    // Whitout legend
    if (!opt.enableLegend) {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(80, 80, 80);
      doc.setLineWidth(borderWidth);
      doc.rect(
        margin,
        paperSpec.heightMM - margin - maxHeight - offset - 2,
        computedWidth + 2,
        maxHeight + 2,
        "FD"
      );
      if (pngDataUrl) {
        doc.addImage(
          pngDataUrl,
          "PNG",
          margin + 1,
          paperSpec.heightMM - margin - maxHeight - offset - 1,
          computedWidth,
          maxHeight
        );
      }
    } else {
      // With legend
      if (pngDataUrl) {
        doc.addImage(
          pngDataUrl,
          "PNG",
          margin,
          paperSpec.heightMM - margin - maxHeight - offset,
          computedWidth,
          maxHeight
        );
      }
    }
    // TODO: Få tekst ind
    doc.text(metaData.printFooter, margin, paperSpec.heightMM - margin);

    // save
    if (!withoutSave) {
      let docTitle = metaData.estateName ? metaData.estateName : "";
      if (metaData.activityName)
        docTitle += (docTitle ? " - " : "") + metaData.activityName;
      else if (metaData.projectName)
        docTitle += (docTitle ? " - " : "") + metaData.projectName;
      else if (metaData.mapName)
        docTitle += (docTitle ? " - " : "") + metaData.mapName;
      if (!docTitle) docTitle = metaData.intlText.map;
      doc.save(docTitle + ".pdf");
    }

    // reset after we’re done composing the PDF
    map.flyTo(center, zoom, { animate: false });
    return doc;
  }
};

// entering print mode means that that print area will be drawn on the map
export const enterPrintMode = (map: L.Map, opt: PrintOptions): PrintMode => {
  opt.scaleRatios.sort(compareScaleRatio);

  const pane = map.getPane(PRINT_PANE_NAME);
  if (!pane) {
    const newPane = map.createPane(PRINT_PANE_NAME);
    newPane.style.zIndex = PRINT_PAN_Z_INDEX;
    newPane.style.display = "initial";
  } else {
    pane.style.display = "initial";
  }

  const { bounds, scaleRatio } = printAreaCoords(map, opt);
  const main = L.rectangle(bounds, PRINT_STYLE).addTo(map);
  const sidebar = L.rectangle(
    fixBoundsWidth(map, bounds, opt, opt.enableLegend ? SIDE_BAR_WIDTH : 0),
    PRINT_STYLE
  ).addTo(map);
  const scaleRatioMarker = L.marker(main.getBounds().getSouthWest(), {
    icon: divIcon(showScaleRatio(scaleRatio)),
    pane: PRINT_PANE_NAME,
  }).addTo(map);

  const printArea: PrintArea = { main, sidebar, scaleRatioMarker };

  const lazyRedraw = () => redraw(map, opt, printArea);

  let latestScaleRatio: ScaleRatio = scaleRatio;
  const updateScaleRatio = () => {
    const newSR = lazyRedraw();
    if (newSR) latestScaleRatio = newSR;
  };

  map.on("move", updateScaleRatio);
  map.on("zoom", updateScaleRatio);

  const exitPrintMode = () => {
    map.off("move", updateScaleRatio);
    map.off("zoom", updateScaleRatio);
    const pane = map.getPane(PRINT_PANE_NAME);
    if (pane) pane.style.display = "none";
    map.removeLayer(main);
    map.removeLayer(sidebar);
    map.removeLayer(scaleRatioMarker);
  };

  return {
    exit: exitPrintMode,
    printArea,
    redraw: lazyRedraw,
    print: (legendElements, scaleRatioObj, metaData, withoutSave) =>
      print(
        map,
        opt,
        printArea,
        legendElements,
        scaleRatioObj ?? latestScaleRatio,
        metaData,
        withoutSave
      ),
  };
};

// --- Helpers --- //
// Wait for next animation frame
const raf = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

// Wait for Leaflet move/zoom to finish and DOM to reflow
async function settleView(
  map: L.Map,
  {
    softDelayMs = 0,
    safetyMs = 1000,
  }: { softDelayMs?: number; safetyMs?: number } = {}
) {
  await new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      map.off("moveend", finish);
      map.off("zoomend", finish);
      resolve();
    };
    // 1) Fallback: next frame + optional micro delay => resolves even if no events fire
    requestAnimationFrame(() => {
      setTimeout(finish, softDelayMs);
    });
    // 2) Real events if an animation actually occurs
    map.once("moveend", finish);
    map.once("zoomend", finish);
    // 3) Safety timeout so we never hang
    setTimeout(finish, safetyMs);
  });

  // Let layout catch up
  map.invalidateSize(false);
  await raf();
}

function getLayerLabel(layer: L.Layer): string {
  const anyLayer = layer as any;
  return (
    anyLayer.options?.label ||
    anyLayer.options?.name ||
    anyLayer.options?.title ||
    anyLayer._url ||
    anyLayer._tileUrl ||
    "Unnamed layer"
  );
}

function attachPrintLayerMonitor(
  layer: L.GridLayer,
  health: LayerTempHealth,
  opts: {
    failRatio: number;
    minErrorTiles: number;
    consecutiveFailsRequired: number;
    watchdogMs: number;
  }
) {
  let ok = 0;
  let bad = 0;

  let pending = 0;
  let lastEventTs = Date.now();

  let cycleActive = false;
  let watchdog: number | null = null;

  const anyLayer = layer as any;

  const clearTimers = () => {
    if (watchdog) window.clearTimeout(watchdog);
    watchdog = null;
  };

  const beginCycleIfNeeded = (why: string) => {
    if (cycleActive) return;
    cycleActive = true;
    ok = 0;
    bad = 0;
    pending = Math.max(pending, 0);
    lastEventTs = Date.now();

    clearTimers();

    watchdog = window.setTimeout(() => {
      // ✅ Only fail on stall if we're actually loading / have pending tiles
      const stillLoading = pending > 0 || !!anyLayer._loading;
      const since = Date.now() - lastEventTs;

      if (stillLoading && since >= opts.watchdogMs) {
        evalCycle(`Stalled while loading (${why})`);
      }
    }, opts.watchdogMs);
  };

  const evalCycle = (reasonHint?: string) => {
    if (!cycleActive) return;
    cycleActive = false;
    clearTimers();

    const total = ok + bad;

    // ✅ IMPORTANT: Do NOT fail on "no activity".
    // Only fail on meaningful signals:
    // 1) enough errors, or
    // 2) high error ratio (when we have enough samples), or
    // 3) explicit stall reason while loading
    const cycleFailed =
      // A single failed tile with no successful tiles is enough evidence that
      // this layer cannot contribute to this print. Requiring four failures
      // left small print extents vulnerable to one broken WMS request.
      (ok === 0 && bad > 0) ||
      (total >= opts.minErrorTiles && bad / total >= opts.failRatio) ||
      (!!reasonHint && reasonHint.toLowerCase().includes("stalled"));

    if (cycleFailed) {
      health.consecutiveFailCycles++;
      health.lastReason =
        reasonHint ??
        (ok === 0 ? "No successful tiles" : "High tile error ratio");

      if (health.consecutiveFailCycles >= opts.consecutiveFailsRequired) {
        health.failed = true;
      }
    } else {
      health.consecutiveFailCycles = 0;
    }

    health.ok += ok;
    health.bad += bad;
  };

  const bump = () => {
    lastEventTs = Date.now();
  };

  const onLoading = () => {
    bump();
    beginCycleIfNeeded("loading");
  };

  const onTileLoadStart = () => {
    pending++;
    bump();
    beginCycleIfNeeded("tileloadstart");
  };

  const onTileLoad = () => {
    pending = Math.max(0, pending - 1);
    ok++;
    bump();

    if (pending === 0 && !anyLayer._loading) {
      // Layer believes it’s done; close the cycle (success case)
      evalCycle();
    }
  };

  const onTileError = () => {
    pending = Math.max(0, pending - 1);
    bad++;
    bump();

    if (pending === 0 && !anyLayer._loading) {
      evalCycle();
    }
  };

  const onLoad = () => {
    bump();
    evalCycle();
  };

  layer.on("loading", onLoading);
  layer.on("tileloadstart", onTileLoadStart);
  layer.on("tileload", onTileLoad);
  layer.on("tileerror", onTileError);
  layer.on("load", onLoad);

  // ✅ If we attach while already loading, start a cycle so stall watchdog can trigger
  if (anyLayer._loading) {
    beginCycleIfNeeded("_loading=true at attach");
  }

  return () => {
    clearTimers();
    layer.off("loading", onLoading);
    layer.off("tileloadstart", onTileLoadStart);
    layer.off("tileload", onTileLoad);
    layer.off("tileerror", onTileError);
    layer.off("load", onLoad);
  };
}

function patchTileTimeoutForPrint(layer: L.GridLayer, tileTimeoutMs: number) {
  const anyLayer = layer as any;
  const originalCreateTile = anyLayer.createTile?.bind(layer);

  if (!originalCreateTile) return () => {};

  anyLayer.createTile = (coords: any, done: any) => {
    let finished = false;

    // Wrap done so we can detect if the original layer ever calls it
    const wrappedDone = (err: any, tile: any) => {
      if (finished) return;
      finished = true;
      try {
        done(err, tile);
      } catch {
        // swallow
      }
    };

    const tile = originalCreateTile(coords, wrappedDone);

    // Only handle <img> tiles
    if (!(tile instanceof HTMLImageElement)) return tile;

    const clear = () => {
      if (timer) window.clearTimeout(timer);
      tile.removeEventListener("load", onLoad);
      tile.removeEventListener("error", onError);
    };

    const onLoad = () => {
      clear();
      // ✅ Ensure done is called even if original implementation didn't
      wrappedDone(null, tile);
    };

    const onError = () => {
      clear();
      const err = new Error("Tile request errored (print)");
      wrappedDone(err, tile);
      // Leaflet will usually fire tileerror internally, but some implementations won’t
      layer.fire("tileerror", { error: err, tile, coords });
    };

    tile.addEventListener("load", onLoad);
    tile.addEventListener("error", onError);

    const timer = window.setTimeout(() => {
      if (finished) return;
      clear();

      try {
        // abort the request
        tile.src = "";
      } catch {}

      const err = new Error("Tile request timed out (print)");
      wrappedDone(err, tile);
      layer.fire("tileerror", { error: err, tile, coords });
    }, tileTimeoutMs);

    return tile;
  };

  return () => {
    anyLayer.createTile = originalCreateTile;
  };
}

async function excludeBadLayersForPrint(map: L.Map, opt: PrintOptions) {
  const TILE_TIMEOUT_MS = 10_000;
  const WAIT_ALL_TILES_TIMEOUT_MS = 20_000; // >= TILE_TIMEOUT_MS
  const WATCHDOG_MS = 8_000;
  const QUIET_MS = 350;

  const FAIL_RATIO = 0.7;
  const MIN_ERROR_TILES = 4;
  const CONSEC_FAILS = 1;

  const allTileLayers = getVisibleTileLayers(map);

  // ✅ Only consider WMS-like overlays for exclusion.
  const candidates = allTileLayers.filter(isEligibleForPrintExclusion);

  if (candidates.length === 0) {
    // Nothing eligible; return a no-op restore
    return {
      removed: [] as PrintExclusion[],
      timedOut: [] as PrintLayerTimeout[],
      restore: () => {},
    };
  }

  const healthByLayer = new Map<L.GridLayer, LayerTempHealth>();
  const detachFns: Array<() => void> = [];

  for (const layer of candidates) {
    const health: LayerTempHealth = {
      label: getLayerLabel(layer),
      ok: 0,
      bad: 0,
      timedOut: 0,
      consecutiveFailCycles: 0,
      failed: false,
    };
    healthByLayer.set(layer, health);

    // ✅ Patch timeouts only for candidates (WMS-like overlays)
    const unpatch = patchTileTimeoutForPrint(layer, TILE_TIMEOUT_MS);

    const detachMonitor = attachPrintLayerMonitor(layer, health, {
      failRatio: FAIL_RATIO,
      minErrorTiles: MIN_ERROR_TILES,
      consecutiveFailsRequired: CONSEC_FAILS,
      watchdogMs: WATCHDOG_MS,
    });

    detachFns.push(unpatch, detachMonitor);
  }

  // Do not call redraw() here, as it starts a second complete WMS request
  // cycle for every generated screening map page.
  const layerResults = await Promise.all(
    candidates.map((t) =>
      waitForTileLayerQuiet(t, {
        timeout: WAIT_ALL_TILES_TIMEOUT_MS,
        quietMs: QUIET_MS,
      })
    )
  );
  await raf();

  const timedOut = layerResults
    .filter((result) => result.status === "timedOut")
    .map((result) => ({ label: getLayerLabel(result.layer) }));

  // A layer which never becomes quiet is not safe to hand to dom-to-image.
  // Treat it like a failed layer for this one capture, while restoring it to
  // the live map immediately afterwards.
  for (const result of layerResults) {
    if (result.status !== "timedOut") continue;
    const health = healthByLayer.get(result.layer);
    if (!health) continue;
    health.failed = true;
    health.lastReason = "Timed out while loading for print";
  }

  const removed: PrintExclusion[] = [];

  for (const layer of candidates) {
    const health = healthByLayer.get(layer)!;
    if (!health.failed) continue;

    const ex: PrintExclusion = {
      layer,
      label: health.label,
      reason: health.lastReason ?? "Layer failed during print",
      ok: health.ok,
      bad: health.bad,
    };

    if (map.hasLayer(layer)) {
      map.removeLayer(layer);
      removed.push(ex);
    }
  }

  await raf();

  const restore = () => {
    for (const r of removed) {
      if (!map.hasLayer(r.layer)) map.addLayer(r.layer);
    }
    detachFns.forEach((fn) => fn());
  };

  return { removed, timedOut, restore };
}

// Collect visible tile-like layers (XYZ, WMS, grid)
function getVisibleTileLayers(map: L.Map): L.TileLayer[] {
  const layers: L.TileLayer[] = [];
  map.eachLayer((layer) => {
    // WMS extends TileLayer; GridLayer also has tile events, but we mostly care about TileLayer
    const anyLayer = layer as any;
    const isTile =
      layer instanceof L.TileLayer || layer instanceof (L.TileLayer as any).WMS;
    const inMap = map.hasLayer(layer);
    const visible =
      !anyLayer._container || anyLayer._container.style.display !== "none";
    if (isTile && inMap && visible) layers.push(layer as L.TileLayer);
  });
  return layers;
}

function getLayerLabelsForImageUri(map: L.Map, uri: string): string[] {
  const labels: string[] = [];

  for (const layer of getVisibleTileLayers(map)) {
    const tiles = (layer as any)._tiles as
      | Record<string, { el?: HTMLImageElement }>
      | undefined;
    if (!tiles) continue;

    const containsImage = Object.values(tiles).some(({ el }) => {
      const src = el?.currentSrc || el?.src;
      return src === uri;
    });
    if (containsImage) labels.push(getLayerLabel(layer));
  }

  return labels;
}

function isWmsLikeTileLayer(layer: L.TileLayer): boolean {
  // Robust: works for Leaflet WMS and most WMS wrappers
  const anyLayer = layer as any;

  // Leaflet built-in WMS class
  const WMS = (L.TileLayer as any).WMS;
  if (WMS && layer instanceof WMS) return true;

  // Many WMS layers expose wmsParams
  if (anyLayer.wmsParams) return true;

  // Heuristic fallback: WMS-ish options (NOT URL string matching)
  const opt = anyLayer.options ?? {};
  const hasLayersOpt = typeof opt.layers === "string" && opt.layers.length > 0;
  const hasFormat = typeof opt.format === "string";
  const hasTransparent = typeof opt.transparent === "boolean";
  const hasStyles = typeof opt.styles === "string";

  return hasLayersOpt && (hasFormat || hasTransparent || hasStyles);
}

/**
 * Decide whether this tile layer is eligible for print exclusion.
 * Default: only WMS-like overlays are eligible.
 * (If you have other flaky overlay types, add more structural checks here.)
 */
function isEligibleForPrintExclusion(layer: L.TileLayer): boolean {
  return (layer as any).options?.printExcludable === true;
  // return isWmsLikeTileLayer(layer);
}

function removeAllPrintExcludableLayers(map: L.Map): PrintExclusion[] {
  const removed: PrintExclusion[] = [];

  for (const layer of getVisibleTileLayers(map)) {
    // The normal health check only uses explicitly marked overlays. At this
    // final fallback point, also cover legacy WMS layers that predate that
    // marker so one provider cannot prevent the rest of the map from printing.
    if (
      (!isEligibleForPrintExclusion(layer) && !isWmsLikeTileLayer(layer)) ||
      !map.hasLayer(layer)
    ) {
      continue;
    }

    map.removeLayer(layer);
    removed.push({
      layer,
      label: getLayerLabel(layer),
      reason: "Removed after the map snapshot failed",
      ok: 0,
      bad: 0,
    });
  }

  return removed;
}

function restoreRemovedLayers(map: L.Map, removed: PrintExclusion[]) {
  for (const exclusion of removed) {
    if (!map.hasLayer(exclusion.layer)) map.addLayer(exclusion.layer);
  }
}

// Wait for a single tile layer to finish loading *and* stay quiet for a quiet period
type TileLayerWaitResult = {
  layer: L.TileLayer;
  status: "ready" | "timedOut";
};

function waitForTileLayerQuiet(
  layer: L.TileLayer,
  {
    timeout = 10000,
    quietMs = 300,
    stallMs = 0, // if > 0: "no activity while still loading" => resolve
  }: { timeout?: number; quietMs?: number; stallMs?: number } = {}
): Promise<TileLayerWaitResult> {
  return new Promise<TileLayerWaitResult>((resolve) => {
    let pending = 0;
    let lastEventTs = Date.now();
    let quietTimer: number | null = null;
    let stallTimer: number | null = null;
    let done = false;

    const anyLayer = layer as any;

    const cleanup = () => {
      layer.off("tileloadstart", onLoadStart);
      layer.off("tileload", onTileLoad);
      layer.off("tileerror", onTileError);

      if (quietTimer) clearTimeout(quietTimer);
      if (stallTimer) clearTimeout(stallTimer);
      if (to) clearTimeout(to);

      quietTimer = null;
      stallTimer = null;
    };

    let status: TileLayerWaitResult["status"] = "ready";

    const finish = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve({ layer, status });
    };

    const finishIfQuiet = () => {
      if (done) return;
      const since = Date.now() - lastEventTs;
      if (since >= quietMs && pending === 0) {
        finish();
      }
    };

    const scheduleQuietCheck = () => {
      if (quietTimer) clearTimeout(quietTimer);
      quietTimer = window.setTimeout(finishIfQuiet, quietMs);
    };

    // ✅ NEW: stall detection
    const scheduleStallCheck = () => {
      if (!stallMs || stallMs <= 0) return;

      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = window.setTimeout(() => {
        if (done) return;

        const stillLoading = pending > 0 || !!anyLayer._loading;
        const since = Date.now() - lastEventTs;

        if (stillLoading && since >= stallMs) {
          status = "timedOut";
          finish();
        }
      }, stallMs);
    };

    const bumpActivity = () => {
      lastEventTs = Date.now();
      scheduleStallCheck();
    };

    const onLoadStart = () => {
      pending++;
      bumpActivity();

      if (quietTimer) {
        clearTimeout(quietTimer);
        quietTimer = null;
      }
    };

    const onTileLoad = () => {
      pending = Math.max(0, pending - 1);
      bumpActivity();

      if (pending === 0) {
        scheduleQuietCheck();
      }
    };

    const onTileError = () => {
      pending = Math.max(0, pending - 1);
      bumpActivity();

      if (pending === 0) {
        scheduleQuietCheck();
      }
    };

    // Attach listeners
    layer.on("tileloadstart", onLoadStart);
    layer.on("tileload", onTileLoad);
    layer.on("tileerror", onTileError);

    // Seed with internal loading state
    if (anyLayer._loading) {
      // We don't know the exact count; treat as "some pending"
      pending = Math.max(pending, 1);
      scheduleStallCheck(); // ✅ important: start stall timer immediately
    } else {
      // Not loading => start quiet timer
      scheduleQuietCheck();
    }

    // Safety timeout (hard cap)
    const to = setTimeout(() => {
      if (done) return;
      status = "timedOut";
      finish();
    }, timeout) as unknown as number;
  });
}

// Wait for all visible tile layers to be quiet
async function waitAllTilesQuiet(
  map: L.Map,
  opts: { timeout?: number; quietMs?: number; stallMs?: number } = {}
): Promise<TileLayerWaitResult[]> {
  const tiles = getVisibleTileLayers(map);
  if (tiles.length === 0) return [];
  const results = await Promise.all(
    tiles.map((t) => waitForTileLayerQuiet(t, opts))
  );
  // One extra raf to ensure images are painted in the DOM
  await raf();
  return results;
}
